// V-11 fix: all imports at the top of the module
import { useState } from "react";

// ─── Header ──────────────────────────────────────────────────────────────────
export function Header({ dorkCount }) {
  return (
    <header className="header">
      <div className="logo">
        <div className="logo-icon">⬡</div>
        ReconDork
        <span className="logo-ver">v2.0</span>
      </div>
      <div className="status-bar">
        <div className="status-dot" />
        <span className="status-text">AI ENGINE ONLINE</span>
        <span className="status-sep">|</span>
        <span className="status-text">{dorkCount} DORKS</span>
      </div>
    </header>
  );
}

// ─── Disclaimer ───────────────────────────────────────────────────────────────
export function Disclaimer() {
  return (
    <div className="disclaimer">
      ⚠ FOR EDUCATIONAL AND LEGAL OSINT USE ONLY — Queries target publicly
      indexed data only. Never use to gain unauthorized access. Ensure legal
      authorization before investigation.
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: "all",           label: "All Types" },
  { id: "exposed_docs",  label: "Exposed Docs" },
  { id: "login_portals", label: "Login Portals" },
  { id: "open_dirs",     label: "Open Directories" },
  { id: "cameras_iot",   label: "Cameras / IoT" },
  { id: "secrets",       label: "Secrets / API Keys" },
];

const PRESETS = [
  { label: "→ Domain Recon",      query: "Domain reconnaissance and asset discovery for target domain" },
  { label: "→ Email Discovery",   query: "Find email addresses and contact info for target organization" },
  { label: "→ Subdomain Enum",    query: "Passive subdomain enumeration and DNS discovery for target" },
  { label: "→ Data Leaks",        query: "Find data leaks, exposed credentials, and breached data for target" },
  { label: "→ Social Footprint",  query: "Social media presence and public profiles for person or org" },
  { label: "→ Tech Fingerprint",  query: "Technology stack, CMS, frameworks, and infrastructure fingerprinting" },
  { label: "→ Backup & Config",   query: "Find exposed backup files, configuration files, and sensitive directories" },
  { label: "→ Database Exposure", query: "Find exposed databases, SQL dumps, and database admin interfaces" },
];

export function Sidebar({ onPresetSelect, setActiveTab }) {
  const [activeCat, setActiveCat] = useState("all");

  const handlePreset = (query) => {
    onPresetSelect(query);
    setActiveTab("generator");
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-section">
        <div className="sidebar-title">Categories</div>
        {CATEGORIES.map((c) => (
          <div
            key={c.id}
            className={`cat-item ${activeCat === c.id ? "active" : ""}`}
            onClick={() => setActiveCat(c.id)}
          >
            <div className="cat-dot" />
            {c.label}
          </div>
        ))}
      </div>
      <div className="sidebar-section">
        <div className="sidebar-title">OSINT Presets</div>
        {PRESETS.map((p) => (
          <button key={p.label} className="preset-btn" onClick={() => handlePreset(p.query)}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="sidebar-section">
        <div className="sidebar-title">Engines</div>
        <div className="engine-list">
          {["Google", "Bing", "DuckDuckGo", "Brave Search"].map((e) => (
            <div key={e} className="engine-item">✓ {e}</div>
          ))}
        </div>
      </div>
    </aside>
  );
}

// (useState imported at top of file — V-11 fixed)

// ─── Dork Reference ───────────────────────────────────────────────────────────
const DORK_DOCS = [
  { op: "site:", desc: "Restrict results to a specific domain or subdomain.", ex: "site:gov.tn filetype:pdf" },
  { op: "inurl:", desc: "Search for a keyword within the URL path.", ex: "inurl:admin/login.php" },
  { op: "intitle:", desc: "Search for a keyword in the HTML page title.", ex: 'intitle:"index of"' },
  { op: "intext:", desc: "Search for a keyword in page body content.", ex: 'intext:"API_KEY"' },
  { op: "filetype:", desc: "Find specific file types indexed by the engine.", ex: "filetype:xlsx site:edu" },
  { op: "ext:", desc: "Alternative to filetype: — identical behavior.", ex: 'ext:sql "password"' },
  { op: '"..."', desc: "Force exact phrase matching in results.", ex: '"confidential" filetype:pdf' },
  { op: "cache:", desc: "View the Google-cached version of a page.", ex: "cache:example.com/login" },
  { op: "-term", desc: "Exclude terms using a minus prefix before the word.", ex: "site:edu -site:mit.edu" },
  { op: "OR", desc: "Logical OR — match either of two terms.", ex: "inurl:login OR inurl:signin" },
  { op: "related:", desc: "Find sites topically similar to a given URL.", ex: "related:github.com" },
  { op: "info:", desc: "Get indexed metadata and info about a URL.", ex: "info:example.com" },
  { op: "allinurl:", desc: "All specified terms must appear in the URL.", ex: "allinurl:admin dashboard" },
  { op: "allintitle:", desc: "All terms must appear in the page title.", ex: "allintitle:login admin panel" },
  { op: "*", desc: "Wildcard — matches any word at that position.", ex: '"password" * "username" *' },
  { op: "before:", desc: "Filter results published before a date (YYYY-MM-DD).", ex: "site:gov before:2020-01-01" },
  { op: "after:", desc: "Filter results published after a date (YYYY-MM-DD).", ex: "filetype:pdf after:2023-01-01" },
  { op: "numrange:", desc: "Search within a numerical range using two dots.", ex: "numrange:8080..8443" },
];

export function DorkReference() {
  return (
    <div>
      <div className="learn-title">⬡ Google Dork Operator Reference</div>
      <div className="learn-grid">
        {DORK_DOCS.map((d) => (
          <div key={d.op} className="learn-card">
            <div className="learn-op">{d.op}</div>
            <div className="learn-desc">{d.desc}</div>
            <div className="learn-example">{d.ex}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Query Chain ──────────────────────────────────────────────────────────────
const ENGINES_FOR_CHAIN = [
  { id: "google",     label: "Google",    url: (q) => `https://www.google.com/search?q=${encodeURIComponent(q)}` },
  { id: "duckduckgo", label: "DuckDuckGo", url: (q) => `https://duckduckgo.com/?q=${encodeURIComponent(q)}` },
];

export function QueryChain({ queries, onRemove, onClear }) {
  // V-04: window.open with noopener,noreferrer prevents tabnabbing —
  // without this, the opened tab can set window.opener.location to
  // redirect the parent page (a classic reverse tabnabbing attack).
  const runChain = (engineFn) => {
    queries.forEach((q, i) => {
      setTimeout(
        () => window.open(engineFn(q), "_blank", "noopener,noreferrer"),
        i * 300
      );
    });
  };

  if (queries.length === 0) {
    return (
      <div className="chain-empty">
        No queries in chain.<br />
        Generate dorks and click <span>[+ CHAIN]</span> to add them here.<br />
        Then execute all at once across search engines.
      </div>
    );
  }

  return (
    <div className="chain-container">
      {queries.map((q, i) => (
        <div key={i} className="chain-item">
          <span className="chain-num">{String(i + 1).padStart(2, "0")}</span>
          <span className="chain-query">{q}</span>
          <button className="chain-del" onClick={() => onRemove(i)} title="Remove">✕</button>
        </div>
      ))}
      <div className="chain-actions">
        {ENGINES_FOR_CHAIN.map((e) => (
          <button key={e.id} className="chain-run-btn" onClick={() => runChain(e.url)}>
            [ EXECUTE ON {e.label.toUpperCase()} ]
          </button>
        ))}
        <button className="chain-clear-btn" onClick={onClear}>CLEAR ALL</button>
      </div>
    </div>
  );
}
